import { Fragment, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SupportPopUp from "../ui/popup/SupportPopUp";
import SharePopUp from "../ui/popup/SharePopUp";
import PrivateRecommendation from "./PrivateRecommendation";
import PrivateMobileNavigation from "./PrivateMobileNavigation";
import GhostPrivateLayout from "../ui/loading/skeletons/global/GhostPrivateLayout";
import { closeSidebar, toggleSidebar } from "../../store/redux_auth/authSlice";
import { getLanguageContent } from "../../utils/language/languageSelector";
import { PUBLIC_NAV_BAR_SCHEMA } from "../../utils/schemas/language/components/publicnavbarSchema";

const PrivateLayout = ({
  children,
  showRightSidebar = true,
  showLeftSidebar = true,
  rightSidebarComponent = null,
  loading = false,
  fullsize = false,
  langKey,
}) => {
  // State for sidebar interactions
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isReferModalOpen, setIsReferModalOpen] = useState(false);
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector((state) => state.auth.isSidebarOpen);
  const location = useLocation();
  const affiliatesEnabled = useSelector(
    (state) => state.auth.user?.affiliates_enabled,
  );
  const affiliateStatus = useSelector(
    (state) => state.auth.user?.affiliate_status,
  );

  const financialInsights = useSelector(
    (state) =>
      state.auth.user?.financial_insights || {
        is_enabled: false,
        personal_finances: false,
      },
  );

  // Show affiliates menu if user has affiliate status OR has access via feature flag
  // const showAffiliates = !!affiliateStatus || !!affiliatesEnabled;
  const showAffiliates = false;
  const content = getLanguageContent(PUBLIC_NAV_BAR_SCHEMA, langKey).pri;
  const affiliateMenuText = affiliateStatus
    ? content.engagement.affiliates.text
    : content.engagement.become_affiliate.text;

  useEffect(() => {
    dispatch(closeSidebar());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    // Cleanup: ensure scroll is restored if component unmounts
    return () => document.body.classList.remove("no-scroll");
  }, [isSidebarOpen]);

  return (
    <Fragment>
      {/* Main Layout */}
      <div className="main-layout">
        <div
          className={`sidebar-overlay ${isSidebarOpen ? "active" : ""}`}
          onClick={() => dispatch(toggleSidebar())}
        />
        {/* Left Sidebar */}
        {loading && <GhostPrivateLayout />}
        {!loading && showLeftSidebar && (
          <aside
            className={`left-sidebar ${isSidebarOpen ? "mobile-active" : ""}`}
          >
            <div className="sidebar-content">
              {/* Primary services navigation */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">{content.services.heading}</h3>
                <ul className="sidebar-menu">
                  <li>
                    {location.pathname.startsWith("/tax") ? (
                      <span
                        className="sidebar-link active"
                        style={{ cursor: "default" }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/filing_blue.svg`}
                          alt={content.services.tax_filing.text}
                          className="sidebar-icon"
                        />
                        {content.services.tax_filing.text}
                      </span>
                    ) : (
                      <Link
                        to="/tax-filing"
                        className="sidebar-link"
                        onMouseEnter={(e) => {
                          const icon =
                            e.currentTarget.querySelector(".sidebar-icon");
                          if (icon)
                            icon.src = `${process.env.PUBLIC_URL}/images/icons/filing_blue.svg`;
                        }}
                        onMouseLeave={(e) => {
                          const icon =
                            e.currentTarget.querySelector(".sidebar-icon");
                          if (icon)
                            icon.src = `${process.env.PUBLIC_URL}/images/icons/filing_grey.svg`;
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/filing_grey.svg`}
                          alt={content.services.tax_filing.text}
                          className="sidebar-icon"
                        />
                        {content.services.tax_filing.text}
                      </Link>
                    )}
                  </li>
                </ul>
              </div>

              {/* Plaintr for Finances */}
              {(financialInsights.is_enabled ||
                financialInsights.personal_finances) && (
                <div className="sidebar-section">
                  <h3 className="sidebar-title">
                    {content.financial_insights.heading}
                  </h3>
                  <ul className="sidebar-menu">
                    {/* INSIGHTS HUB LINK */}
                    <li>
                      {location.pathname === "/finances/insights" ? (
                        <span
                          className="sidebar-link active"
                          style={{ cursor: "default" }}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/images/icons/insights_blue_menu.svg`}
                            alt={content.financial_insights.insights_hub.text}
                            className="sidebar-icon"
                          />
                          {content.financial_insights.insights_hub.text}
                        </span>
                      ) : (
                        <Link
                          to="/finances/insights"
                          className="sidebar-link"
                          onMouseEnter={(e) => {
                            const icon =
                              e.currentTarget.querySelector(".sidebar-icon");
                            if (icon)
                              icon.src = `${process.env.PUBLIC_URL}/images/icons/insights_blue_menu.svg`;
                          }}
                          onMouseLeave={(e) => {
                            const icon =
                              e.currentTarget.querySelector(".sidebar-icon");
                            if (icon)
                              icon.src = `${process.env.PUBLIC_URL}/images/icons/insights_grey_menu.svg`;
                          }}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/images/icons/insights_grey_menu.svg`}
                            alt={content.financial_insights.insights_hub.text}
                            className="sidebar-icon"
                          />
                          {content.financial_insights.insights_hub.text}
                        </Link>
                      )}
                    </li>

                    {/* PERSONAL FINANCES LINK */}
                    {financialInsights.personal_finances && (
                      <li>
                        {location.pathname.startsWith(
                          "/finances/personal-finances",
                        ) ? (
                          <span
                            className="sidebar-link active"
                            style={{ cursor: "default" }}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/images/icons/finance_graph_blue.svg`}
                              alt={
                                content.financial_insights.personal_finances
                                  .text
                              }
                              className="sidebar-icon"
                            />
                            {content.financial_insights.personal_finances.text}
                          </span>
                        ) : (
                          <Link
                            to="/finances/personal-finances/overview"
                            className="sidebar-link"
                            onMouseEnter={(e) => {
                              const icon =
                                e.currentTarget.querySelector(".sidebar-icon");
                              if (icon)
                                icon.src = `${process.env.PUBLIC_URL}/images/icons/finance_graph_blue.svg`;
                            }}
                            onMouseLeave={(e) => {
                              const icon =
                                e.currentTarget.querySelector(".sidebar-icon");
                              if (icon)
                                icon.src = `${process.env.PUBLIC_URL}/images/icons/finance_graph_grey.svg`;
                            }}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/images/icons/finance_graph_grey.svg`}
                              alt={
                                content.financial_insights.personal_finances
                                  .text
                              }
                              className="sidebar-icon"
                            />
                            {content.financial_insights.personal_finances.text}
                          </Link>
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Engagement navigation */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">{content.engagement.heading}</h3>
                <ul className="sidebar-menu">
                  <li>
                    <button
                      className="sidebar-link"
                      onClick={() => setIsReferModalOpen(true)}
                      onMouseEnter={(e) => {
                        const icon =
                          e.currentTarget.querySelector(".sidebar-icon");
                        if (icon)
                          icon.src = `${process.env.PUBLIC_URL}/images/icons/friend_blue.svg`;
                      }}
                      onMouseLeave={(e) => {
                        const icon =
                          e.currentTarget.querySelector(".sidebar-icon");
                        if (icon)
                          icon.src = `${process.env.PUBLIC_URL}/images/icons/friend_grey.svg`;
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icons/friend_grey.svg`}
                        alt={content.engagement.refer_friend.text}
                        className="sidebar-icon"
                      />
                      {content.engagement.refer_friend.text}
                    </button>
                  </li>
                  <li>
                    {location.pathname === "/blog" ? (
                      <span
                        className="sidebar-link active"
                        style={{ cursor: "default" }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/news_blue.svg`}
                          alt={content.engagement.decoded_plaintr.text}
                          className="sidebar-icon"
                        />
                        {content.engagement.decoded_plaintr.text}
                      </span>
                    ) : (
                      <Link
                        to="/blog"
                        className="sidebar-link"
                        onMouseEnter={(e) => {
                          const icon =
                            e.currentTarget.querySelector(".sidebar-icon");
                          if (icon)
                            icon.src = `${process.env.PUBLIC_URL}/images/icons/news_blue.svg`;
                        }}
                        onMouseLeave={(e) => {
                          const icon =
                            e.currentTarget.querySelector(".sidebar-icon");
                          if (icon)
                            icon.src = `${process.env.PUBLIC_URL}/images/icons/news_grey.svg`;
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/news_grey.svg`}
                          alt={content.engagement.decoded_plaintr.text}
                          className="sidebar-icon"
                        />
                        {content.engagement.decoded_plaintr.text}
                      </Link>
                    )}
                  </li>
                  {showAffiliates && (
                    <li>
                      {location.pathname.startsWith("/affiliates") ? (
                        <span
                          className="sidebar-link active"
                          style={{ cursor: "default" }}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/images/icons/handshake_blue.svg`}
                            alt={affiliateMenuText}
                            className="sidebar-icon"
                          />
                          {affiliateMenuText}
                        </span>
                      ) : (
                        <Link
                          to="/affiliates"
                          className="sidebar-link"
                          onMouseEnter={(e) => {
                            const icon =
                              e.currentTarget.querySelector(".sidebar-icon");
                            if (icon)
                              icon.src = `${process.env.PUBLIC_URL}/images/icons/handshake_blue.svg`;
                          }}
                          onMouseLeave={(e) => {
                            const icon =
                              e.currentTarget.querySelector(".sidebar-icon");
                            if (icon)
                              icon.src = `${process.env.PUBLIC_URL}/images/icons/handshake_grey.svg`;
                          }}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/images/icons/handshake_grey.svg`}
                            alt={affiliateMenuText}
                            className="sidebar-icon"
                          />
                          {affiliateMenuText}
                        </Link>
                      )}
                    </li>
                  )}
                </ul>
              </div>

              {/* Support navigation */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">
                  {content.help_support.heading}
                </h3>
                <ul className="sidebar-menu">
                  <li>
                    <button
                      className="sidebar-link"
                      onClick={() => setIsSupportModalOpen(true)}
                      onMouseEnter={(e) => {
                        const icon =
                          e.currentTarget.querySelector(".sidebar-icon");
                        if (icon)
                          icon.src = `${process.env.PUBLIC_URL}/images/icons/support_blue.svg`;
                      }}
                      onMouseLeave={(e) => {
                        const icon =
                          e.currentTarget.querySelector(".sidebar-icon");
                        if (icon)
                          icon.src = `${process.env.PUBLIC_URL}/images/icons/support_grey.svg`;
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icons/support_grey.svg`}
                        alt={content.help_support.contact_support.text}
                        className="sidebar-icon"
                      />
                      {content.help_support.contact_support.text}
                    </button>
                  </li>
                  <li>
                    {location.pathname === "/help-center" ? (
                      <span
                        className="sidebar-link active"
                        style={{ cursor: "default" }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/help_blue.svg`}
                          alt={content.help_support.help_center.text}
                          className="sidebar-icon"
                        />
                        {content.help_support.help_center.text}
                      </span>
                    ) : (
                      <Link
                        to="/help-center"
                        className="sidebar-link"
                        onMouseEnter={(e) => {
                          const icon =
                            e.currentTarget.querySelector(".sidebar-icon");
                          if (icon)
                            icon.src = `${process.env.PUBLIC_URL}/images/icons/help_blue.svg`;
                        }}
                        onMouseLeave={(e) => {
                          const icon =
                            e.currentTarget.querySelector(".sidebar-icon");
                          if (icon)
                            icon.src = `${process.env.PUBLIC_URL}/images/icons/help_grey.svg`;
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/help_grey.svg`}
                          alt={content.help_support.help_center.text}
                          className="sidebar-icon"
                        />
                        {content.help_support.help_center.text}
                      </Link>
                    )}
                  </li>
                </ul>
              </div>

              {/* White Separator */}
              <hr className="separator-white" />

              {/* Social Networks */}
              <div className="sidebar-section">
                <div className="social-links">
                  <Link
                    to="https://www.linkedin.com/company/plaintr/?viewAsMember=true"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/ln_white.svg`}
                      alt="LinkedIn"
                      className="social-icon"
                      onMouseEnter={(e) =>
                        (e.target.src = `${process.env.PUBLIC_URL}/images/icons/ln_blue.svg`)
                      }
                      onMouseLeave={(e) =>
                        (e.target.src = `${process.env.PUBLIC_URL}/images/icons/ln_white.svg`)
                      }
                    />
                  </Link>
                  <Link
                    to="https://www.facebook.com/profile.php?id=61580473952631"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/fb_white.svg`}
                      alt="Facebook"
                      className="social-icon"
                      onMouseEnter={(e) =>
                        (e.target.src = `${process.env.PUBLIC_URL}/images/icons/fb_blue.svg`)
                      }
                      onMouseLeave={(e) =>
                        (e.target.src = `${process.env.PUBLIC_URL}/images/icons/fb_white.svg`)
                      }
                    />
                  </Link>
                  <Link
                    to="https://www.instagram.com/plaintrllc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/inst_white.svg`}
                      alt="Instagram"
                      className="social-icon"
                      onMouseEnter={(e) =>
                        (e.target.src = `${process.env.PUBLIC_URL}/images/icons/inst_blue.svg`)
                      }
                      onMouseLeave={(e) =>
                        (e.target.src = `${process.env.PUBLIC_URL}/images/icons/inst_white.svg`)
                      }
                    />
                  </Link>
                </div>
              </div>

              {/* Bottom Spacer */}
              <div style={{ height: "50px" }}></div>
            </div>
          </aside>
        )}

        {/* Main Content - Route Content Goes Here */}
        <main
          className={`main-content ${fullsize ? "fullwidth-container" : ""}`}
        >
          {children}

          {/* Right Sidebar - Render based on showRightSidebar and rightSidebarComponent */}
          {showRightSidebar &&
            (rightSidebarComponent ? (
              <aside>{rightSidebarComponent}</aside>
            ) : (
              <aside className="right-sidebar">
                <PrivateRecommendation />
              </aside>
            ))}
        </main>
      </div>

      {/* Mobile Navigation - Hidden on desktop */}
      <div className="mobile-nav-container">
        <PrivateMobileNavigation />
      </div>

      {isSupportModalOpen && (
        <SupportPopUp setIsModalOpen={setIsSupportModalOpen} />
      )}
      {isReferModalOpen && <SharePopUp setIsModalOpen={setIsReferModalOpen} />}
    </Fragment>
  );
};

export default PrivateLayout;
